import React, { ReactNode, useEffect, useState } from 'react';
import { Header } from '@epo/epods-react-components/lib/Components/Header';
import { Drawer } from '@epo/epods-react-components/lib/Components/Drawer';
import { EPOLogoBase64 } from '@epo/epods-react-components/lib/Components/Header';
import { Dropdown } from '@epo/epods-react-components/lib/Components/Dropdown';
import { useSelector, useDispatch } from 'react-redux';
import { AppDispatch, selectors, actions } from '../store/store';
import { Language } from '../types/enums';
import { changeDropdownLanguage } from '../hooks/change-dropdown-language.hook';
import { isEmpty } from 'lodash';
import useTranslation from '../hooks/use-translation.hook';
import { setStorageItem } from '../helpers/storage';
import useQueryParams from '../hooks/useQuery';
import { useLocation } from 'react-router-dom';

type LayoutProps = {
  children: ReactNode;
};

const MainLayout: React.FC<LayoutProps> = ({ children }: LayoutProps) => {
  const location = useLocation();
  const { hash, pathname, search } = location;

  const { query, setQueryParams } = useQueryParams();
  const { formatMessage: m } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const [activeUtility, setActiveUtility] = useState(false);
  const [hasAppliedUrlLang, setHasAppliedUrlLang] = useState(false);

  const language = useSelector(selectors.settings.selectLanguage);
  const rigiLanguage = useSelector(selectors.settings.selectRigiLanguage);

  const displayLanguage = isEmpty(rigiLanguage) ? language : rigiLanguage;

  const listLanguages = [
    { label: Language.EN, id: Language.EN },
    { label: Language.FR, id: Language.FR },
    { label: Language.DE, id: Language.DE },
  ];

  const footerLinks = [
    {
      label: m({ id: 'footer.legalNotice' }),
      href: 'https://www.epo.org/en/legal-notice',
    },
    {
      label: m({ id: 'footer.termsOfUse' }),
      href: 'https://www.epo.org/en/terms-of-use',
    },
    {
      label: m({ id: 'footer.dataProtection' }),
      href: 'https://www.epo.org/en/about-us/office/data-protection-and-privacy',
    },
    {
      label: m({ id: 'footer.accesibility' }),
      href: 'https://www.epo.org/en/accessibility',
    },
  ];

  useEffect(() => {
    const rawLangParam = query.get('lang');
    const langParam = rawLangParam?.toUpperCase() as Language;
    const isValidLanguage = Object.values(Language).includes(langParam);

    if (!hasAppliedUrlLang && isValidLanguage && langParam !== language) {
      dispatch(actions.settings.updateLanguage(langParam));
      dispatch(actions.settings.updateRigiLanguage(''));
      setStorageItem('language', langParam);
      localStorage.setItem('lang', langParam);

      changeDropdownLanguage(langParam, langParam, langParam);

      // Remove query lang URL to avoid re-trigger this effect
      const updatedParams: Record<string, string> = {};
      query.forEach((value, key) => {
        if (key !== 'lang') {
          updatedParams[key] = value;
        }
      });

      setQueryParams(updatedParams);
      setHasAppliedUrlLang(true);
    }
  }, [query, language, hasAppliedUrlLang, dispatch]);

  const RightSideControls = () => {
    const defaultConfig = {
      create: false,
      notifications: false,
      userProfile: false,
    };
    const [activeKeyDrawer, setActiveKeyDrawer] = React.useState(defaultConfig);

    return (
      <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
        <Dropdown
          id='epo-language-dropdown'
          name='epo-language-dropdown'
          data-nua='language-dropdown'
          preset='select'
          size='default'
          aria-label={m({
            id: 'applicationMenu.languageDropdown.item.ariaLabel',
          })}
          maxDropdownHeight='300px'
          value={[{ id: displayLanguage }]}
          onChange={(params: any) => {
            const newLang = params.option.id;
            changeDropdownLanguage(newLang, newLang, newLang);

            dispatch(actions.settings.updateLanguage(newLang));
            dispatch(actions.settings.updateRigiLanguage(''));
            setStorageItem('language', newLang);
            localStorage.setItem('lang', newLang);

            const currentParams: Record<string, string> = {};
            query.forEach((value, key) => {
              currentParams[key] = value;
            });
            setQueryParams({
              ...currentParams,
              lang: newLang,
            });
          }}
          options={listLanguages}
          ControlContainerStyle={{ width: '80px' }}
        />
      </div>
    );
  };

  return (
    <div className='w-100 vh-100'>
      <div
        className='w-100 h-100 overflow-hidden bg-canvas text-primary d-flex flex-column'
        style={{ zIndex: 1 }}
      >
        <Header
          logo={EPOLogoBase64}
          appName={`${m({ id: 'header.title' })}`}
          leftSide={<></>}
          rightSide={<RightSideControls />}
          backgroundColor='white'
          zIndex={9999}
          className='p-s'
        />
        <div className='container bg-body flex-grow-1 mx-0 scrollable-y'>
          <div className='App bg-light container-fluid'>
            <div className='container-fluid' style={{ maxWidth: '1450px' }}>
              <div
                className='p-xl'
                style={{ background: '#fff', minHeight: '95vh' }}
              >
                {children}
              </div>
            </div>
          </div>
        </div>
        <div className='text-inverted bg-ds-dark p-l'>
          <div
            className='d-flex mx-m'
            style={{ gap: 'var(--epods--space-inset-l)' }}
          >
            {footerLinks.map((linkItem, index) => (
              <a
                className='text-inverted'
                key={index}
                href={linkItem.href}
                target='_blank'
                rel='noopener noreferrer'
              >
                {linkItem.label}
              </a>
            ))}
          </div>
        </div>
      </div>
      <div style={{ zIndex: 2 }}>
        <Drawer isOpen={activeUtility} onClose={() => setActiveUtility(false)}>
          <h2>Drawer Content</h2>
        </Drawer>
      </div>
    </div>
  );
};

export default MainLayout;
